import { Component, type ErrorInfo, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';

import { TEXT_COLORS } from '@/common/presentation/constants/colors';

interface Props {
  instanceId: string;
  blockKind?: string;
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export class BlockRenderErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[BlockRenderErrorBoundary] block render failed', {
      instanceId: this.props.instanceId,
      blockKind: this.props.blockKind,
      error,
      componentStack: info.componentStack
    });

    Sentry.captureException(error, {
      tags: {
        boundary: 'BlockRenderErrorBoundary',
        instanceId: this.props.instanceId,
        blockKind: this.props.blockKind
      },
      contexts: {
        react: {
          componentStack: info.componentStack
        }
      }
    });
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.instanceId !== this.props.instanceId && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        role="alert"
        data-testid={`document-render:block-error:${this.props.instanceId}`}
        style={{
          padding: 12,
          margin: '8px 0',
          border: `1px dashed ${TEXT_COLORS.MUTED}`,
          color: TEXT_COLORS.MUTED,
          fontSize: 13
        }}
      >
        This block could not be previewed because its saved data is incomplete or invalid.
      </div>
    );
  }
}
